/* cspell:ignore overscan */
import { useVirtualizer } from "@tanstack/react-virtual";
import * as React from "react";

export type UseVirtualizeOptions<T> = {
	/**
	 * Array of items to virtualize
	 */
	items: T[];
	/**
	 * Height of each item in pixels
	 * @default 32
	 */
	itemHeight?: number;
	/**
	 * Maximum height of the container in pixels
	 * @default 240
	 */
	maxHeight?: number;
	/**
	 * Minimum height of the container in pixels
	 * @default itemHeight
	 */
	minHeight?: number;
	/**
	 * Number of items to render outside the visible area for smoother scrolling
	 * @default 5
	 */
	overscan?: number;
	/**
	 * Whether virtualization is enabled
	 * @default true
	 */
	enabled?: boolean;
	/**
	 * Whether keyboard navigation is active
	 * @default false
	 */
	isKeyboardNavActive?: boolean;
	/**
	 * Currently focused item index
	 * @default -1
	 */
	focusedIndex?: number;
	/**
	 * Callback when focused index changes
	 */
	onFocusedIndexChange?: (index: number) => void;
	/**
	 * Callback when keyboard navigation state changes
	 */
	onKeyboardNavActiveChange?: (active: boolean) => void;
	/**
	 * Callback when an item is selected via keyboard
	 */
	onItemSelect?: (item: T, index: number) => void;
	/**
	 * Whether to automatically force measure when the dropdown opens
	 * @default false
	 */
	autoForceMeasure?: boolean;
	/**
	 * Whether the dropdown is currently open (used with autoForceMeasure)
	 * @default false
	 */
	isOpen?: boolean;
};

export type UseVirtualizeReturn = {
	/**
	 * Ref to attach to the scrollable container
	 */
	parentRef: React.RefObject<HTMLDivElement | null>;
	/**
	 * Virtualizer instance from @tanstack/react-virtual
	 */
	virtualizer: ReturnType<typeof useVirtualizer<HTMLDivElement, Element>>;
	/**
	 * Currently focused item index
	 */
	focusedIndex: number;
	/**
	 * Whether keyboard navigation is active
	 */
	isKeyboardNavActive: boolean;
	/**
	 * Calculated height of the dropdown container
	 */
	containerHeight: number;
	/**
	 * Scroll to a specific item index
	 */
	scrollToIndex: (index: number) => void;
	/**
	 * Handle keyboard events for navigation
	 */
	handleKeyDown: (event: React.KeyboardEvent) => void;
	/**
	 * Handle mouse enter on an item
	 */
	handleItemMouseEnter: (index: number) => void;
	/**
	 * Handle mouse leave on an item
	 */
	handleItemMouseLeave: () => void;
	/**
	 * Force virtualizer to recalculate measurements
	 */
	forceMeasure: () => void;
};

/**
 * useVirtualize - A hook for virtualizing large lists with keyboard navigation
 *
 * This hook provides virtualization functionality with built-in keyboard navigation,
 * scroll management, and dynamic height calculation. It's designed to work with
 * dropdown components that need to handle large lists efficiently.
 *
 * @example
 * ```tsx
 * const {
 *   parentRef,
 *   virtualizer,
 *   focusedIndex,
 *   isKeyboardNavActive,
 *   containerHeight,
 *   scrollToIndex,
 *   handleKeyDown,
 *   handleItemMouseEnter,
 *   handleItemMouseLeave,
 *   forceMeasure
 * } = useVirtualize({
 *   items: options,
 *   itemHeight: 32,
 *   maxHeight: 240,
 *   autoForceMeasure: true,
 *   isOpen: isDropdownOpen,
 *   onItemSelect: (item, index) => {
 *     onChange(item.value);
 *     setIsOpen(false);
 *   }
 * });
 * ```
 */
export const useVirtualize = <T,>({
	items,
	itemHeight = 32,
	maxHeight = 240,
	minHeight,
	overscan = 5,
	enabled = true,
	isKeyboardNavActive: externalKeyboardNavActive,
	focusedIndex: externalFocusedIndex,
	onFocusedIndexChange,
	onKeyboardNavActiveChange,
	onItemSelect,
	autoForceMeasure = false,
	isOpen = false,
}: UseVirtualizeOptions<T>): UseVirtualizeReturn => {
	// Internal state for keyboard navigation
	const [internalKeyboardNavActive, setInternalKeyboardNavActive] = React.useState(false);
	const [internalFocusedIndex, setInternalFocusedIndex] = React.useState(-1);

	// Use external state if provided, otherwise use internal state
	const isKeyboardNavActive = externalKeyboardNavActive ?? internalKeyboardNavActive;
	const focusedIndex = externalFocusedIndex ?? internalFocusedIndex;

	// Ref for the scrollable container
	const parentRef = React.useRef<HTMLDivElement>(null);

	// Calculate dynamic height based on items
	const containerHeight = React.useMemo(() => {
		if (!enabled) return maxHeight;

		const calculatedMinHeight = minHeight ?? itemHeight;
		const calculatedHeight = Math.min(items.length * itemHeight, maxHeight);
		return Math.max(calculatedHeight, calculatedMinHeight);
	}, [items.length, itemHeight, maxHeight, minHeight, enabled]);

	// Virtualization setup
	const virtualizer = useVirtualizer({
		count: items.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => itemHeight,
		enabled,
		overscan,
		initialRect: { width: 0, height: containerHeight },
	});

	// Scroll to a specific index
	const scrollToIndex = React.useCallback(
		(index: number) => {
			virtualizer.scrollToIndex(index, {
				align: "center",
			});
		},
		[virtualizer]
	);

	// Handle keyboard navigation
	const handleKeyDown = React.useCallback(
		(event: React.KeyboardEvent) => {
			switch (event.key) {
				case "ArrowDown": {
					event.preventDefault();
					const newKeyboardNavActive = true;
					const newIndex = focusedIndex === -1 ? 0 : Math.min(focusedIndex + 1, items.length - 1);

					// Update state
					if (externalKeyboardNavActive === undefined) {
						setInternalKeyboardNavActive(newKeyboardNavActive);
					}
					if (externalFocusedIndex === undefined) {
						setInternalFocusedIndex(newIndex);
					}

					// Call callbacks
					onKeyboardNavActiveChange?.(newKeyboardNavActive);
					onFocusedIndexChange?.(newIndex);

					// Scroll to the focused item
					scrollToIndex(newIndex);
					break;
				}
				case "ArrowUp": {
					event.preventDefault();
					const newKeyboardNavActive = true;
					const newIndex = focusedIndex === -1 ? items.length - 1 : Math.max(focusedIndex - 1, 0);

					// Update state
					if (externalKeyboardNavActive === undefined) {
						setInternalKeyboardNavActive(newKeyboardNavActive);
					}
					if (externalFocusedIndex === undefined) {
						setInternalFocusedIndex(newIndex);
					}

					// Call callbacks
					onKeyboardNavActiveChange?.(newKeyboardNavActive);
					onFocusedIndexChange?.(newIndex);

					// Scroll to the focused item
					scrollToIndex(newIndex);
					break;
				}
				case "Enter": {
					event.preventDefault();
					if (items[focusedIndex] && onItemSelect) {
						onItemSelect(items[focusedIndex], focusedIndex);
					}
					break;
				}
				default:
					break;
			}
		},
		[
			focusedIndex,
			items,
			onItemSelect,
			onKeyboardNavActiveChange,
			onFocusedIndexChange,
			scrollToIndex,
			externalKeyboardNavActive,
			externalFocusedIndex,
		]
	);

	// Handle mouse enter on an item
	const handleItemMouseEnter = React.useCallback(
		(index: number) => {
			if (!isKeyboardNavActive) {
				if (externalFocusedIndex === undefined) {
					setInternalFocusedIndex(index);
				}
				onFocusedIndexChange?.(index);
			}
		},
		[isKeyboardNavActive, onFocusedIndexChange, externalFocusedIndex]
	);

	// Handle mouse leave on an item
	const handleItemMouseLeave = React.useCallback(() => {
		if (!isKeyboardNavActive) {
			if (externalFocusedIndex === undefined) {
				setInternalFocusedIndex(-1);
			}
			onFocusedIndexChange?.(-1);
		}
	}, [isKeyboardNavActive, onFocusedIndexChange, externalFocusedIndex]);

	// Force virtualizer to recalculate measurements
	const forceMeasure = React.useCallback(() => {
		if (parentRef.current) {
			// Force the virtualizer to recalculate by updating its options
			virtualizer.setOptions({
				...virtualizer.options,
				initialRect: { width: 0, height: containerHeight },
			});
			// Also try calling measure directly
			virtualizer.measure();
		}
	}, [virtualizer, containerHeight]);

	// Reset focused index when items change
	React.useEffect(() => {
		if (focusedIndex >= items.length) {
			if (externalFocusedIndex === undefined) {
				setInternalFocusedIndex(-1);
			}
			onFocusedIndexChange?.(-1);
		}
	}, [items.length, focusedIndex, onFocusedIndexChange, externalFocusedIndex]);

	// Auto force measure when dropdown opens (if enabled)
	React.useEffect(() => {
		if (autoForceMeasure && isOpen && enabled) {
			// Force the virtualizer to recalculate measurements with a small delay
			const timeoutId = setTimeout(() => {
				const frameId = requestAnimationFrame(() => {
					forceMeasure();
				});
				return () => cancelAnimationFrame(frameId);
			}, 10); // Small delay to ensure DOM is ready

			return () => clearTimeout(timeoutId);
		}
	}, [autoForceMeasure, isOpen, enabled, forceMeasure]);

	return {
		parentRef,
		virtualizer,
		focusedIndex,
		isKeyboardNavActive,
		containerHeight,
		scrollToIndex,
		handleKeyDown,
		handleItemMouseEnter,
		handleItemMouseLeave,
		forceMeasure,
	};
};
